import React, { useState, useEffect, useRef, useCallback } from "react";

export const SCAN_MODES = [
  { key: "color", label: "Color" },
  { key: "mono", label: "Mono" },
];

// Basis-ritme voor de toolbar: krap (4px) binnen één logische cluster,
// ruimer (≈4·φ, afgerond) tussen twee verschillende clusters — zo oogt de
// scheiding tussen groepen als een bewuste keuze i.p.v. toeval, zonder dat er
// ergens een zware rand voor nodig is.
const GAP = 4;
const GROUP_GAP = 6; // ≈ GAP * 1.618
// Hoogte van de items in de toolbars onderin: 16px regel + 2x8px padding.
// De pil wordt daarmee 32 + 2x2 padding + rand = 38px.
const BOTTOM_ITEM_HEIGHT = 32;
// Ruim genoeg voor de zachte glow van een actieve "small" knop (blur tot 8px)
// om altijd volledig te passen, links én rechts in de cluster - anders voelt
// de cluster onrustig/wisselend van maat, doordat de laatste knop (bv.
// "Page") weinig ademruimte heeft tot de rand terwijl een knop in het midden
// (bv. "Width") wél ruimte had. Met deze marge maakt het niet uit welke knop
// er toevallig actief/hovered is.
const CLUSTER_PAD = 4;

// Een cluster (Color/Mono, bestandsnavigatie, pdf-controls, of de PDF/Files-
// knop zelf) toont/verbergt zich in twee stappen, en dat is bewust de HELE
// truc - niets ingewikkelders:
//
// 1. `display: none/flex` regelt of de cluster layout-ruimte inneemt. Dit
//    gebeurt INSTANT (geen transitie) - en dat is precies waarom het nooit
//    kan haperen: er wordt maar één keer gereflowed, niet 60x per seconde
//    zoals bij het animeren van width/max-width (dat wél steeds hapering gaf,
//    hoeveel timing-logica er ook omheen gebouwd werd).
// 2. Binnen die (al instant aanwezige) ruimte doet `opacity` + `transform:
//    scale()` de eigenlijke, zichtbare animatie - dat zijn compositor/GPU-
//    eigenschappen, dus altijd soepel, ongeacht wat er verder op de pagina
//    gebeurt. Dat geeft het iOS-achtige "pop in" gevoel.
//
// Geen gemeten breedtes, geen clip-path, geen uitgestelde inklap-state meer -
// gewoon twee simpele, onafhankelijke eigenschappen die precies doen waar ze
// goed in zijn.
// Opacity is snel klaar (de inhoud moet er meteen "zijn"); transform loopt
// bewust langer door met een sterkere overshoot-curve, zodat de veer ook
// echt te zien is nadat de inhoud al zichtbaar is - dat "iets" langer
// doorveren ná het verschijnen is wat het als een bounce laat aanvoelen i.p.v.
// alleen een snelle in-fade.
const POP_TRANSITION = "opacity 160ms ease, transform 340ms cubic-bezier(0.32, 1.8, 0.5, 1)";

// Vertraagt het écht zichtbaar worden (revealed) één frame t.o.v. het
// beschikbaar komen van de layout-ruimte (show) - zonder die frame ertussen
// heeft de browser nooit twee verschillende waarden om tussen te animeren en
// verschijnt de cluster instant i.p.v. dat 'ie inpopt. Bij het weer verbergen
// hoeft dat niet: revealed volgt show dan direct (geen aparte exit-animatie -
// dat is een bewuste, simpele keuze, zie de component zelf).
function usePop(show) {
  const [revealed, setRevealed] = useState(show);
  useEffect(() => {
    if (!show) {
      setRevealed(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(raf);
  }, [show]);
  return revealed;
}

function IconUp({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 10L8 5L13 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconDown({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 6L8 11L13 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPrintFilled() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path
        d="M4.66658 11.3335H11.3333V14.6668H4.66658V11.3335ZM12.6666 13.3335V10.0002H3.33325V13.3335H1.99992C1.63173 13.3335 1.33325 13.035 1.33325 12.6668V6.00016C1.33325 5.63198 1.63173 5.3335 1.99992 5.3335H13.9999C14.3681 5.3335 14.6666 5.63198 14.6666 6.00016V12.6668C14.6666 13.035 14.3681 13.3335 13.9999 13.3335H12.6666ZM3.33325 6.66683V8.00016H5.33325V6.66683H3.33325ZM4.66658 1.3335H11.3333C11.7015 1.3335 11.9999 1.63198 11.9999 2.00016V4.00016H3.99992V2.00016C3.99992 1.63198 4.2984 1.3335 4.66658 1.3335Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Ronde printknop rechtsboven bij PDF's, zelfde stijl als de sluitknop
// linksboven. Alleen zichtbaar als er een pdf in de viewer staat.
// Op een touchscherm geen printknop (printen vanaf telefoon/tablet is niet
// nodig); desktop ongewijzigd
const IS_TOUCH_SCREEN = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

export function PdfPrintButton({ visible, disabled, onClick }) {
  const [hovered, setHovered] = useState(false);
  if (IS_TOUCH_SCREEN) return null;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title="Print"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        width: "48px",
        height: "48px",
        padding: "16px",
        boxSizing: "border-box",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "100px",
        border: "1px solid rgba(255, 255, 255, 0.10)",
        background: hovered && !disabled ? "rgba(255, 255, 255, 0.20)" : "rgba(255, 255, 255, 0.10)",
        backdropFilter: "blur(4px)",
        color: "#FFF",
        cursor: disabled ? "default" : "pointer",
        outline: "none",
        ...toolbarPop(visible),
        opacity: visible ? (disabled ? 0.4 : 1) : 0,
        transition: "opacity 160ms ease, transform 340ms cubic-bezier(0.32, 1.8, 0.5, 1), background 300ms ease",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <IconPrintFilled />
    </button>
  );
}

function IconLeft({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconRight({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Glow van onderen (radial gradient + dun lijntje) achter een geselecteerd item.
// Parent moet position: relative hebben. De halo blijft binnen de ronde vorm
// van het item en het lijntje begint/eindigt iets naar binnen: anders steekt
// bij het eerste/laatste item (bv. Mono) een kant buiten de ronde rand van de
// toolbar uit en lijkt de glow scheef.
function GlowBackdrop({ active }) {
  return (
    <>
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          background: "radial-gradient(50% 100% at 50% 100%, rgba(255, 255, 255, 0.20) 0%, rgba(255, 255, 255, 0.00) 100%)",
          opacity: active ? 1 : 0,
          transition: "opacity 250ms ease",
        }}
      />
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: "8px",
          right: "8px",
          bottom: 0,
          height: "1px",
          pointerEvents: "none",
          background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 25%, #FFF 50%, rgba(255,255,255,0.35) 75%, rgba(255,255,255,0) 100%)",
          opacity: active ? 0.6 : 0,
          transition: "opacity 250ms ease",
        }}
      />
    </>
  );
}

// Item in de toolbars onderin. Geselecteerd: witte tekst met een glow van
// onderen (radial gradient + dun lijntje); niet geselecteerd: 40% opacity.
export function GlowSegment({ label, active, onClick, children, title }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        boxSizing: "border-box",
        height: `${BOTTOM_ITEM_HEIGHT}px`,
        padding: "8px 12px",
        borderRadius: "100px",
        border: "none",
        outline: "none",
        background: hovered && !active ? "rgba(255, 255, 255, 0.05)" : "transparent",
        cursor: "pointer",
        whiteSpace: "nowrap",
        color: "#FFF",
        fontFamily: '"DM Sans", sans-serif',
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "16px",
        opacity: active ? 1 : hovered ? 0.85 : 0.4,
        transition: "opacity 200ms ease, background 150ms ease",
        userSelect: "none",
        flexShrink: 0,
      }}
    >
      <GlowBackdrop active={active} />
      <span style={{ position: "relative", display: "flex", alignItems: "center", gap: "4px" }}>
        {children}
        {label}
      </span>
    </button>
  );
}

// Niet-klikbaar label-icoon in de toolbar, gedimd als een niet-actieve knop
export function ToolbarIcon({ padLeft, children }) {
  return (
    <span style={{ display: "flex", alignItems: "center", padding: `0 4px 0 ${padLeft}px`, color: "#FFF", opacity: 0.4, pointerEvents: "none" }}>
      {children}
    </span>
  );
}

// Oog-icoon (resources/icons/view_black.svg): Front/Top/Side bij Scans en
// Fit width/Fit page bij PDF's gaan allebei over hoe je kijkt.
export function ViewIcon({ padLeft = 10 }) {
  return (
    <ToolbarIcon padLeft={padLeft}>
      <svg width="15" height="12" viewBox="0 0 15 12" fill="none" style={{ flexShrink: 0 }}>
        <path
          d="M0 6C0.627007 2.58651 3.61765 0 7.21244 0C10.8072 0 13.7978 2.58651 14.4248 6C13.7978 9.41347 10.8072 12 7.21244 12C3.61765 12 0.627007 9.41347 0 6ZM7.21244 9.33333C9.05337 9.33333 10.5458 7.84093 10.5458 6C10.5458 4.15905 9.05337 2.66667 7.21244 2.66667C5.37147 2.66667 3.87908 4.15905 3.87908 6C3.87908 7.84093 5.37147 9.33333 7.21244 9.33333ZM7.21244 8C6.10784 8 5.21241 7.1046 5.21241 6C5.21241 4.8954 6.10784 4 7.21244 4C8.31697 4 9.21244 4.8954 9.21244 6C9.21244 7.1046 8.31697 8 7.21244 8Z"
          fill="currentColor"
        />
      </svg>
    </ToolbarIcon>
  );
}

function IconBtn({ onClick, disabled, title, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "24px",
        height: "24px",
        padding: 0,
        borderRadius: "50%",
        border: "none",
        outline: "none",
        cursor: disabled ? "default" : "pointer",
        background: hovered && !disabled ? "rgba(255,255,255,0.05)" : "transparent",
        color: disabled ? "rgba(255,255,255,0.15)" : hovered ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.4)",
        fontFamily: "DM Sans, sans-serif",
        fontSize: "15px",
        lineHeight: 1,
        flexShrink: "0",
        transition: "background 150ms ease, color 150ms ease",
      }}
    >
      {children}
    </button>
  );
}

// Ronde knop (32px, licht vlak) voor de pdf-toolbar, met een gevuld icoon
function RoundBtn({ onClick, disabled, title, padding = "8px", children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        width: "32px",
        height: "32px",
        padding,
        boxSizing: "border-box",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "32px",
        border: "none",
        outline: "none",
        background: hovered && !disabled ? "rgba(255, 255, 255, 0.10)" : "rgba(255, 255, 255, 0.05)",
        color: disabled ? "rgba(255, 255, 255, 0.15)" : hovered ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.4)",
        cursor: disabled ? "default" : "pointer",
        flexShrink: 0,
        transition: "background 150ms ease, color 150ms ease",
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
        {children}
      </svg>
    </button>
  );
}

// Printknop in de pdf-toolbar: even groot als de zoom-cirkels (32px), maar
// zonder achtergrond tot je eroverheen gaat
function PrintBtn({ onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label="Print"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        width: "32px",
        height: "32px",
        padding: 0,
        boxSizing: "border-box",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "32px",
        border: "none",
        outline: "none",
        background: hovered && !disabled ? "rgba(255, 255, 255, 0.10)" : "transparent",
        color: disabled ? "rgba(255, 255, 255, 0.15)" : hovered ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.4)",
        cursor: disabled ? "default" : "pointer",
        flexShrink: 0,
        transition: "background 150ms ease, color 150ms ease",
      }}
    >
      <IconPrintFilled />
    </button>
  );
}

// Pagina-knop met een driehoekje; omhoog is hetzelfde driehoekje 180° gedraaid
function PageBtn({ up = false, ...props }) {
  return (
    <RoundBtn padding="8px 7.5px 8px 8.5px" {...props}>
      <path d="M8 10.6667L4 6.66669H12L8 10.6667Z" fill="currentColor" transform={up ? "rotate(180 8 8)" : undefined} />
    </RoundBtn>
  );
}

// Bestand-knop: hetzelfde driehoekje, naar links of rechts gedraaid
function FileBtn({ left = false, ...props }) {
  return (
    <RoundBtn {...props}>
      <path d="M8 10.6667L4 6.66669H12L8 10.6667Z" fill="currentColor" transform={left ? "rotate(90 8 8)" : "rotate(-90 8 8)"} />
    </RoundBtn>
  );
}

function ZoomBtn({ plus = false, ...props }) {
  return (
    <RoundBtn {...props}>
      <path
        d={plus
          ? "M7.3335 7.33331V3.33331H8.66683V7.33331H12.6668V8.66665H8.66683V12.6666H7.3335V8.66665H3.3335V7.33331H7.3335Z"
          : "M3.3335 7.33331H12.6668V8.66665H3.3335V7.33331Z"}
        fill="currentColor"
      />
    </RoundBtn>
  );
}

function ZoomPctBtn({ value, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      title="Reset naar 100% (0)"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        // Vaste breedte (past "800%", de max zoom) + gelijk brede cijfers,
        // zodat de toolbar niet meeverspringt tijdens het zoomen
        width: "48px", height: "24px",
        padding: "0", boxSizing: "border-box",
        fontVariantNumeric: "tabular-nums",
        border: "none", outline: "none", borderRadius: "100px",
        background: hovered ? "rgba(255,255,255,0.05)" : "transparent", cursor: "pointer",
        fontFamily: "DM Sans, sans-serif", fontSize: "12px", fontWeight: 500,
        color: hovered ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.9)",
        flexShrink: "0", whiteSpace: "nowrap",
        transition: "background 150ms ease, color 150ms ease",
      }}
    >
      {Math.round(value * 100)}%
    </button>
  );
}

// Kleine, altijd-even-brede teller ("2/5", "12/34", "—") — gedeeld tussen de
// bestands- en pagina-navigatie zodat beide exact hetzelfde ritme aanhouden.
// pad: extra ruimte links/rechts tussen de tekst en de ronde knoppen
function CounterLabel({ text, pad = 0 }) {
  return (
    <span
      style={{
        padding: `0 ${pad}px`,
        fontFamily: "DM Sans, sans-serif", fontSize: "12px", fontWeight: 500,
        color: "rgba(255,255,255,0.75)", minWidth: "34px", textAlign: "center",
        userSelect: "none", flexShrink: "0", whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

// Verticaal streepje. Puur instant tonen/verbergen (display) - een 1px lijn
// die in-/uitpopt is geen moment waard om apart te animeren.
// Dubbel streepje tussen de groepen in de toolbars onderin
export function Divider({ visible = true }) {
  return (
    <svg
      aria-hidden
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={{ display: visible ? "block" : "none", flexShrink: 0 }}
    >
      <path d="M6 2H7V14H6V2Z" fill="#FFFFFF33" />
      <path d="M9 2H10V14H9V2Z" fill="#FFFFFF33" />
    </svg>
  );
}

// Eén cluster: instant layout (display) + zacht inpoppende inhoud (opacity +
// scale). transformOrigin "left" laat 'm vanuit de rand ernaast (de divider)
// lijken open te vouwen i.p.v. vanuit het midden op te bollen.
function Cluster({ show, gap, marginLeft, marginRight, pad = CLUSTER_PAD, height = BOTTOM_ITEM_HEIGHT, transition = POP_TRANSITION, children }) {
  const revealed = usePop(show);
  return (
    <div
      style={{
        display: show ? "flex" : "none",
        alignItems: "center",
        gap: gap != null ? `${gap}px` : undefined,
        height: `${height}px`,
        padding: `0 ${pad}px`,
        marginLeft: marginLeft ? `${GROUP_GAP}px` : "0px",
        marginRight: marginRight ? `${GROUP_GAP}px` : "0px",
        opacity: revealed ? 1 : 0,
        transform: revealed ? "scale(1)" : "scale(0.85)",
        transformOrigin: "left center",
        pointerEvents: show ? "auto" : "none",
        transition,
      }}
    >
      {children}
    </div>
  );
}

// Bovenste toolbar (Scans / IOS Form / Attachments), volgens het ontwerp.
const TOP_ITEM_HEIGHT = 42;
const TOP_PILL_STYLE = {
  display: "inline-flex",
  padding: "2px",
  alignItems: "flex-start",
  gap: "2px",
  borderRadius: "100px",
  border: "1px solid rgba(255, 255, 255, 0.10)",
  background: "rgba(255, 255, 255, 0.10)",
  backdropFilter: "blur(8px)",
};

// De bovenste pil verandert van breedte als de ene case wel IOS Form/Files
// heeft en de andere niet. Die breedte wordt (op de pil zelf) geanimeerd:
// de inhoud meten we met een ResizeObserver en de pil krijgt die breedte als
// expliciete width met een transitie, zodat 'ie vanuit het midden soepel
// groeit/krimpt i.p.v. te verspringen. Kleine, losstaande pil, dus de
// reflow per frame is hier geen probleem. Op false = oude gedrag (instant).
const ANIMATE_TOP_PILL_WIDTH = true;
// Groeien met dezelfde veer als POP_TRANSITION (~18% voorbij en terug).
// Krimpen met een mildere veer (~3%): daar schiet de pil ónder z'n
// eindbreedte, en met de volle veer zou de inhoud er dan een paar pixels
// buiten steken; ~3% valt binnen de padding + rand.
const TOP_PILL_GROW_TRANSITION = "width 340ms cubic-bezier(0.32, 1.8, 0.5, 1)";
const TOP_PILL_SHRINK_TRANSITION = "width 340ms cubic-bezier(0.32, 1.4, 0.5, 1)";
// Een segment dat erbij komt popt pas in als de pil al deels open is, anders
// steekt het even buiten de nog smalle pil uit.
const TOP_POP_TRANSITION = "opacity 160ms ease 120ms, transform 340ms cubic-bezier(0.32, 1.8, 0.5, 1) 120ms";
// Laatst gemeten breedte, buiten de component: de drawer remount de pil
// (via key) bij een case-wissel vanaf Files, en dan moet 'ie vanaf de oude
// breedte animeren i.p.v. meteen op de nieuwe te staan.
let lastTopPillWidth = null;

function useAnimatedPillWidth() {
  const [width, setWidth] = useState(lastTopPillWidth);
  const [growing, setGrowing] = useState(true);
  const observerRef = useRef(null);
  // Callback-ref: de pil kan weg (return null) en later terugkomen
  const innerRef = useCallback((el) => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = null;
    if (!el || !ANIMATE_TOP_PILL_WIDTH || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const w = el.offsetWidth;
      const prev = lastTopPillWidth;
      if (prev != null && w !== prev) setGrowing(w > prev);
      lastTopPillWidth = w;
      setWidth(w);
    };
    measure();
    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(el);
  }, []);
  return {
    innerRef,
    width: ANIMATE_TOP_PILL_WIDTH ? width : null,
    widthTransition: growing ? TOP_PILL_GROW_TRANSITION : TOP_PILL_SHRINK_TRANSITION,
  };
}

// Zelfde paperclip als op de bijlagetegels in CaseDetailDrawer; currentColor
// zodat hij de tekstkleur (wit / zwart als actief) volgt.
function PaperclipIcon() {
  return (
    <svg width="14" height="15" viewBox="0 0 12 13" fill="none" style={{ flexShrink: 0 }}>
      <path
        d="M7.96653 3.80474L4.19526 7.57596C3.93491 7.83629 3.93491 8.25842 4.19526 8.51876C4.45561 8.77916 4.87773 8.77916 5.13806 8.51876L8.90933 4.74754C9.69033 3.9665 9.69033 2.70017 8.90933 1.91912C8.12826 1.13807 6.86193 1.13807 6.08086 1.91912L2.30964 5.69036C1.00789 6.99209 1.00789 9.10262 2.30964 10.4044C3.61139 11.7062 5.72193 11.7062 7.02366 10.4044L10.7949 6.63316L11.7377 7.57596L7.96653 11.3472C6.14406 13.1696 3.18928 13.1696 1.36683 11.3472C-0.455612 9.52476 -0.455612 6.57002 1.36683 4.74754L5.13806 0.97631C6.4398 -0.325437 8.5504 -0.325437 9.85213 0.97631C11.1539 2.27806 11.1539 4.3886 9.85213 5.69036L6.08086 9.46162C5.29986 10.2426 4.0335 10.2426 3.25245 9.46162C2.4714 8.68056 2.4714 7.41422 3.25245 6.63316L7.02366 2.86193L7.96653 3.80474Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Zelfde vorm als het pdf-icoon op de drawer-knop; currentColor zodat hij
// de tekstkleur (wit / zwart als actief) volgt.
function PdfFormIcon() {
  return (
    <svg width="14" height="16" viewBox="0 0 12 14" fill="none" style={{ flexShrink: 0 }}>
      <path
        d="M1.64486e-05 4L4.00202 0H11.332C11.7 0 12 0.303333 12 0.661333V12.672C11.9998 12.8475 11.93 13.0157 11.8059 13.1397C11.6818 13.2637 11.5135 13.3333 11.338 13.3333H0.662016C0.574468 13.3327 0.487897 13.3149 0.407247 13.2808C0.326597 13.2467 0.253447 13.1971 0.191974 13.1348C0.130501 13.0724 0.0819093 12.9986 0.0489732 12.9175C0.016037 12.8364 -0.000598536 12.7495 1.64486e-05 12.662V4ZM4.66668 1L1.00002 4.66667H4.66668V1Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Logo van het scanner-portal (zelfde als op de scanner-badge). De logo's
// zijn wit; als masker volgt hij currentColor (wit / zwart als actief).
// Bij een ander portal (vorige/volgende case): eerst uitfaden, dan wisselen
// en weer infaden.
const PORTAL_LOGO_FADE_MS = 300;
function PortalLogoIcon({ src }) {
  const [shownSrc, setShownSrc] = useState(src);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (src === shownSrc) {
      setVisible(true);
      return;
    }
    setVisible(false);
    const t = setTimeout(() => setShownSrc(src), PORTAL_LOGO_FADE_MS);
    return () => clearTimeout(t);
  }, [src, shownSrc]);

  return (
    <span
      style={{
        width: "16px",
        height: "16px",
        flexShrink: 0,
        backgroundColor: "currentColor",
        WebkitMask: `url("${shownSrc}") center / contain no-repeat`,
        mask: `url("${shownSrc}") center / contain no-repeat`,
        opacity: visible ? 1 : 0,
        transition: `opacity ${PORTAL_LOGO_FADE_MS}ms ease`,
      }}
    />
  );
}

// Zelfde icoon als de STL-download op de drawer (resources/icons/cad.svg)
function StlScanIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path
        d="M20.5021 5.92225L12 1L3.49793 5.92225L12 10.8445L20.5021 5.92225ZM2.5 7.6555V17.5L11 22.4211V12.5765L2.5 7.6555ZM13 22.4211L21.5 17.5V7.6555L13 12.5765V22.4211Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Zelfde icoon als de PLY-download (resources/icons/3d-with-color-spark.svg),
// voor Scans in Color-modus. Het sterretje houdt z'n paarse verloop.
function PlyScanIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path
        d="M9.51157 1.54175C8.9823 2.22003 8.6665 3.07306 8.6665 4.00008C8.6665 6.20922 10.4574 8.00008 12.6665 8.00008C13.2615 8.00008 13.8257 7.86915 14.3332 7.63615V11.6667L7.99984 15.3334L1.6665 11.6667V4.33341L7.99984 0.666748L9.51157 1.54175ZM2.99984 10.8979L7.99984 13.7931V8.00008L2.99984 5.1049V10.8979Z"
        fill="currentColor"
      />
      <path
        d="M12.3529 1.54617C12.4706 1.26228 12.8629 1.26227 12.9805 1.54617L13.1491 1.95372C13.4371 2.64892 13.9744 3.20407 14.6498 3.50451L15.1283 3.71739C15.4018 3.83929 15.4018 4.23741 15.1283 4.35932L14.6218 4.58458C13.9633 4.87744 13.4356 5.41291 13.1426 6.08523L12.9779 6.46219C12.8576 6.73828 12.4758 6.73828 12.3555 6.46219L12.1914 6.08523C11.8984 5.41281 11.3702 4.87747 10.7116 4.58458L10.2051 4.35932C9.93163 4.23743 9.93163 3.83927 10.2051 3.71739L10.6836 3.50451C11.359 3.20407 11.8964 2.64893 12.1843 1.95372L12.3529 1.54617Z"
        fill="url(#fcs-ply-spark)"
      />
      <defs>
        <linearGradient id="fcs-ply-spark" x1="12.5" y1="1" x2="12.5" y2="7" gradientUnits="userSpaceOnUse">
          <stop stopColor="#6C48D8" />
          <stop offset="0.9" stopColor="#BAA3FF" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function TopSegment({ label, icon, active, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        boxSizing: "border-box",
        height: `${TOP_ITEM_HEIGHT}px`,
        padding: "8px 16px",
        justifyContent: "center",
        alignItems: "center",
        gap: "4px",
        borderRadius: "100px",
        border: "none",
        outline: "none",
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontFamily: '"DM Sans", sans-serif',
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "16px",
        // Wit op het witte actieve item zou onleesbaar zijn, dus daar zwart.
        color: active ? "#000" : "#FFF",
        background: active ? "#FFF" : hovered ? "rgba(255,255,255,0.12)" : "transparent",
        boxShadow: active ? "0 4px 16px 0 rgba(255, 255, 255, 0.40)" : "none",
        transition: "background 150ms ease, color 250ms ease, box-shadow 250ms ease",
        userSelect: "none",
        flexShrink: 0,
      }}
    >
      {icon}
      {label}
    </button>
  );
}

export default function FileCategorySelector({
  category = "scans",
  onCategoryChange,
  showOrderForm = true,
  showMiscFiles = true,
  // "color" → PLY-icoon bij Scans, anders het STL-icoon
  scanMode = null,
  // Logo van het scanner-portal; zonder logo het pdf-icoon
  portalLogo = null,
}) {
  const [localCategory, setLocalCategory] = useState(category);
  // Volgen als de categorie van buitenaf wisselt (Tab in de drawer)
  useEffect(() => { setLocalCategory(category); }, [category]);
  const isScans = localCategory === "scans";
  const isOrderForm = localCategory === "order-form";
  const isMiscFiles = localCategory === "misc-files";
  const { innerRef, width, widthTransition } = useAnimatedPillWidth();
  const clusterTransition = ANIMATE_TOP_PILL_WIDTH ? TOP_POP_TRANSITION : POP_TRANSITION;
  // Zonder PDF/misc-files is er niets om tussen te wisselen; Color/Mono staat
  // in de scan-toolbar onderin, dus dan blijft deze pil helemaal weg.
  if (!showOrderForm && !showMiscFiles) return null;

  const changeCategory = (key) => {
    setLocalCategory(key);
    if (onCategoryChange) onCategoryChange(key);
  };

  return (
    <div
      style={{
        ...TOP_PILL_STYLE,
        // content-box: width = gemeten inhoud, padding/rand komen erbij
        ...(width != null && { width: `${width}px`, boxSizing: "content-box", justifyContent: "center", transition: widthTransition }),
      }}
    >
      <div ref={innerRef} style={{ display: "flex", alignItems: "flex-start", gap: "2px", flexShrink: 0 }}>
      <TopSegment
        label="Scans"
        icon={scanMode === "color" ? <PlyScanIcon /> : <StlScanIcon />}
        active={isScans}
        onClick={() => changeCategory("scans")}
      />
      <Cluster show={showOrderForm} pad={0} height={TOP_ITEM_HEIGHT} transition={clusterTransition}>
        <TopSegment
          label="Form"
          icon={portalLogo ? <PortalLogoIcon src={portalLogo} /> : <PdfFormIcon />}
          active={isOrderForm}
          onClick={() => changeCategory("order-form")}
        />
      </Cluster>
      <Cluster show={showMiscFiles} pad={0} height={TOP_ITEM_HEIGHT} transition={clusterTransition}>
        <TopSegment
          label="Files"
          icon={<PaperclipIcon />}
          active={isMiscFiles}
          onClick={() => changeCategory("misc-files")}
        />
      </Cluster>
      </div>
    </div>
  );
}

// In-/uitpop voor de toolbars onderin (Front/Top/Side bij Scans, en
// FileViewerControls bij PDF/Files), zodat het wisselen tussen die twee
// dezelfde beweging heeft: inpoppen met de veer van POP_TRANSITION, uitpoppen
// snel en zonder veer.
export function toolbarPop(visible, delay = "0s") {
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? "scale(1)" : "scale(0.85)",
    transition: visible
      ? `opacity 160ms ease ${delay}, transform 340ms cubic-bezier(0.32, 1.8, 0.5, 1) ${delay}`
      : "opacity 160ms ease, transform 160ms ease",
  };
}

// Oog-icoon vóór Fit width/Fit page, zoals vóór Front/Top/Side bij Scans.
// Op false = oude gedrag.
const PDF_FIT_ICON = true;

// Bestands-/pagina-navigatie en zoom bij PDF/Files: "1 of 3" i.p.v. "1/3" en ronde knoppen
// met driehoekjes / plus / min i.p.v. chevrons en tekst. Op false = oude gedrag.
const PDF_PAGE_NAV_V2 = true;

// Losse ronde printknop rechtsboven (PdfPrintButton) i.p.v. in de toolbar
// onderin. Op false = printknop in de toolbar na Fit page.
export const PDF_PRINT_TOP_RIGHT = false;

// Gedeelde stijl van de pil-toolbars (ook gebruikt door Dracoloader's
// ViewModeSelector).
export const PILL_STYLE = {
  display: "flex",
  padding: "2px",
  justifyContent: "center",
  alignItems: "center",
  gap: "2px",
  borderRadius: "100px",
  border: "1px solid rgba(255, 255, 255, 0.10)",
  background: "rgba(255, 255, 255, 0.10)",
  backdropFilter: "blur(8px)",
  fontFamily: "DM Sans, sans-serif",
};

// Bestandsnavigatie en pdf-controls voor de PDF- en Files-tab. Staat los van
// de categorie-pil bovenin, onderin op de plek van Front/Top/Side bij Scans.
export function FileViewerControls({
  category = "scans",
  // Bestandsnavigatie binnen "Files" - alleen getoond bij meer dan één bestand.
  fileIndex = -1,
  fileCount = 0,
  onFilePrev,
  onFileNext,
  // Is het nu getoonde bestand (binnen Files) een pdf?
  isCurrentFilePdf = false,
  pdfPageIndex = 0,
  pdfNumPages = 0,
  pdfZoom = 1,
  pdfFitMode = null,
  onPdfPrevPage,
  onPdfNextPage,
  onPdfZoomIn,
  onPdfZoomOut,
  onPdfZoomReset,
  onPdfFitWidth,
  onPdfFitPage,
  // Zelfde actie als de PDF-knop op de drawer (download van de pdf).
  onPdfPrint,
  pdfPrintDisabled = false,
  // Touch-layout: zonder de zoomknoppen (zoomen gaat daar met pinch)
  compact = false,
}) {
  const isOrderForm = category === "order-form";
  const isMiscFiles = category === "misc-files";
  const liveFileNav = isMiscFiles && fileCount > 1;
  const livePdfControls = isOrderForm || (isMiscFiles && isCurrentFilePdf);
  const visible = liveFileNav || livePdfControls;

  // Tijdens het uitfaden de laatst getoonde inhoud vasthouden, anders klapt
  // de pil al leeg in terwijl hij nog zichtbaar is.
  const [shown, setShown] = useState({ fileNav: liveFileNav, pdf: livePdfControls });
  useEffect(() => {
    if (visible) setShown({ fileNav: liveFileNav, pdf: livePdfControls });
  }, [visible, liveFileNav, livePdfControls]);
  const showFileNav = visible ? liveFileNav : shown.fileNav;
  const showPdfControls = visible ? livePdfControls : shown.pdf;
  const showFileNavAndPdf = showFileNav && showPdfControls;

  return (
    <div
      style={{
        ...PILL_STYLE,
        // Ronde knoppen op 2px (boven/onder) en 1px (zijkanten) van de rand van de pil
        ...(PDF_PAGE_NAV_V2 && { padding: "2px 1px" }),
        ...toolbarPop(visible),
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      {/* Bestandsnavigatie komt altijd vóór eventuele pdf-subcontrols. */}
      <Cluster show={showFileNav} gap={GAP} marginRight={showFileNavAndPdf} pad={PDF_PAGE_NAV_V2 ? 0 : CLUSTER_PAD}>
        {PDF_PAGE_NAV_V2 ? (
          <>
            <FileBtn left onClick={onFilePrev} title="Vorige bestand" />
            <CounterLabel text={fileCount > 0 ? `${fileIndex + 1} of ${fileCount}` : "—"} pad={6} />
            <FileBtn onClick={onFileNext} title="Volgende bestand" />
          </>
        ) : (
          <>
            <IconBtn onClick={onFilePrev} title="Vorige bestand">
              <IconLeft />
            </IconBtn>
            <CounterLabel text={fileCount > 0 ? `${fileIndex + 1}/${fileCount}` : "—"} />
            <IconBtn onClick={onFileNext} title="Volgende bestand">
              <IconRight />
            </IconBtn>
          </>
        )}
      </Cluster>

      <Divider visible={showFileNavAndPdf} />

      {/* PDF-subcontrols: pagina-navigatie, zoom, fit-modus. */}
      <Cluster show={showPdfControls} gap={GAP} marginLeft={showFileNavAndPdf} pad={PDF_PAGE_NAV_V2 ? 0 : CLUSTER_PAD}>
        {PDF_PAGE_NAV_V2 ? (
          <>
            <PageBtn up onClick={onPdfPrevPage} disabled={pdfPageIndex <= 0} title="Vorige pagina (PgUp)" />
            <CounterLabel text={pdfNumPages > 0 ? `${pdfPageIndex + 1} of ${pdfNumPages}` : "—"} pad={6} />
            <PageBtn onClick={onPdfNextPage} disabled={pdfPageIndex >= pdfNumPages - 1} title="Volgende pagina (PgDn)" />
          </>
        ) : (
          <>
            <IconBtn onClick={onPdfPrevPage} disabled={pdfPageIndex <= 0} title="Vorige pagina (PgUp)">
              <IconUp />
            </IconBtn>
            <CounterLabel text={pdfNumPages > 0 ? `${pdfPageIndex + 1}/${pdfNumPages}` : "—"} />
            <IconBtn onClick={onPdfNextPage} disabled={pdfPageIndex >= pdfNumPages - 1} title="Volgende pagina (PgDn)">
              <IconDown />
            </IconBtn>
          </>
        )}

        {!compact && <Divider />}

        {/* Zonder Width/Page-fit is de zoom handmatig gekozen; dan staat de glow hier */}
        {!compact && <div
          style={{
            position: "relative", display: "flex", alignItems: "center", gap: `${GAP}px`,
            height: `${BOTTOM_ITEM_HEIGHT}px`, padding: "0 4px", boxSizing: "border-box",
            borderRadius: "100px", flexShrink: 0,
          }}
        >
          <GlowBackdrop active={pdfFitMode !== "width" && pdfFitMode !== "page"} />
          {PDF_PAGE_NAV_V2 ? (
            <>
              <ZoomBtn onClick={onPdfZoomOut} title="Uitzoomen (-)" />
              <ZoomPctBtn value={pdfZoom} onClick={onPdfZoomReset} />
              <ZoomBtn plus onClick={onPdfZoomIn} title="Inzoomen (+)" />
            </>
          ) : (
            <>
              <IconBtn onClick={onPdfZoomOut} title="Uitzoomen (-)">−</IconBtn>
              <ZoomPctBtn value={pdfZoom} onClick={onPdfZoomReset} />
              <IconBtn onClick={onPdfZoomIn} title="Inzoomen (+)">+</IconBtn>
            </>
          )}
        </div>}

        <Divider />

        {PDF_FIT_ICON && <ViewIcon padLeft={6} />}
        <GlowSegment label="Fit width" active={pdfFitMode === "width"} onClick={onPdfFitWidth} />
        <GlowSegment label="Fit page" active={pdfFitMode === "page"} onClick={onPdfFitPage} />

        {/* Net als de losse knop: geen printknop op een touchscherm */}
        {onPdfPrint && !PDF_PRINT_TOP_RIGHT && !IS_TOUCH_SCREEN && (
          <>
            <Divider />
            <PrintBtn onClick={onPdfPrint} disabled={pdfPrintDisabled} />
          </>
        )}
      </Cluster>
    </div>
  );
}
